// ── oddsStreamHub — per-gameKey shared SSE subscription manager ────
//
// Module-level singleton that owns at most ONE EventSource per gameKey
// regardless of how many React consumers ask for the data. Both
// GameDetail's `<ConsensusChart>` view AND the per-source movement
// page at `/game/:date/:a/:b/sources` subscribe through this hub, so
// navigating between them doesn't double up on hydra's 256-listener
// cap.
//
// Refcount-based lifecycle: 0 → 1 opens the EventSource, 1 → 0 closes
// it after a short grace window. The grace window survives React 18
// StrictMode's intentional mount/unmount/remount cycle in dev, and
// also covers a rapid forward-then-back navigation between
// GameDetail and the sources page (no SSE churn).
//
// State exposed per gameKey is immutable: every incoming event clones
// the subscription's `state` object and notifies listeners, so
// `useSyncExternalStore` consumers get stable snapshots between
// renders and only re-render when state actually changed.

import {
  openOddsStream,
  type OddsStreamConsensus,
  type OddsStreamHandlers,
  type OddsStreamConsensusHistoryPoint,
  type OddsStreamGameState,
  type OddsStreamHandle,
  type OddsStreamLine,
  type OddsStreamSnapshot,
} from './oddsStream';
import {
  ingestConsensusTrendFrame,
  trendFrameFromConsensus,
  type ConsensusTrendState,
} from '../lib/consensusTrend';

export type OddsStreamStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'stale'
  | 'closed'
  | 'error'
  /** The optional analytics capability never opened here — no base is
   *  configured, or the EventSource could not be constructed against the one
   *  we have. Distinct from 'error', which is a stream that DID open and then
   *  failed, and which EventSource retries on its own. */
  | 'unavailable';

const FULL_GAME_PERIOD_ID = 1;
const HEARTBEAT_DEDUP_MS = 10_000;
/** Sliding-window retention for per-source point buffers. Powers the
 *  table's sparklines (last N points) AND the /sources page's
 *  multi-book line chart (time-ordered).
 *
 *  30 min is comfortably under what lightweight-charts handles (~50k
 *  points across ~30 series) and keeps the memory profile small
 *  (~30 sources × ~1800 entries × 16 bytes ≈ 1 MB upper bound, far
 *  less in practice). If this ever feels heavy on lower-end hardware,
 *  dial back to 10-15 min. */
const POINT_WINDOW_MS = 30 * 60 * 1000;
const CLOSE_GRACE_MS = 1_000;

/** Single (ts, cents) sample appended to a source's point buffer on
 *  every `line` SSE event. ts is hydra's serverTs for the event so
 *  multiple events with the same wall-clock land in stable order. */
export interface SourcePoint {
  ts: number;
  cents: number;
}

/** Composite key for line lookups: marketSourceId × betTypeId ×
 *  periodTypeId × sideIndex. Stable string for use as a Map key. */
export function lineKeyOf(line: {
  marketSourceId: number;
  betTypeId: number;
  periodTypeId: number;
  sideIndex: number;
}): string {
  return `${line.marketSourceId}|${line.betTypeId}|${line.periodTypeId}|${line.sideIndex}`;
}

/** Consensus bucket key: (periodTypeId, marketType). The soccer knockout
 *  "To Advance" rows (marketType 'advance') share periodTypeId 1 with the
 *  regulation 1X2 result — keying by period alone would let the two
 *  clobber each other (same contract as the WS channel's
 *  `consensusCacheKey` in marketStore). */
function consensusBucketKeyOf(c: Pick<OddsStreamConsensus, 'periodTypeId' | 'marketType'>): string {
  return `${c.periodTypeId}|${c.marketType ?? 'result'}`;
}

/** The Full-Game regulation result row — what the chart, U row, and score
 *  header key on. Excludes the soccer 'advance' rows, which ride the same
 *  periodTypeId. */
export function isFullGameResult(c: OddsStreamConsensus): boolean {
  return c.periodTypeId === FULL_GAME_PERIOD_ID && c.marketType == null;
}

/** Merge an incoming consensus array into the previously-stored one, keyed
 *  by (periodTypeId, marketType). Buckets present in `next` overwrite their
 *  prior entries; buckets present ONLY in `prev` are preserved.
 *
 *  Why: hydra's odds-stream events (consensus, heartbeat) don't always
 *  carry every period each tick. The pre-2026-05-26 code did a full
 *  `latestConsensus = event.consensus` replace, so a heartbeat or
 *  consensus event that landed without a Full-Game (`periodTypeId === 1`)
 *  entry would wipe the consensus row even though the underlying
 *  probability hadn't gone away — exactly the symptom Dan saw on
 *  WSH @ CLE 2026-05-26 where the chart's team labels + the trade
 *  panel's U row both disappeared while `consensusPoints` (the
 *  independent history buffer) kept the chart line alive.
 *
 *  Sticky-merging by bucket preserves the last known value per bucket
 *  until a new sample arrives. Snapshot still does a full replace —
 *  that's the authoritative bootstrap. Game-over staleness is fine
 *  because consumers gate display on `latestGameState.statusId`. */
function mergeConsensusByPeriod(
  prev: OddsStreamConsensus[],
  next: OddsStreamConsensus[],
): OddsStreamConsensus[] {
  if (next.length === 0) return prev;
  const incomingBuckets = new Set(next.map(consensusBucketKeyOf));
  const preserved = prev.filter((c) => !incomingBuckets.has(consensusBucketKeyOf(c)));
  return preserved.length === 0 ? next : [...preserved, ...next];
}

/** Fold a consensus row batch into the per-bucket trend map. Returns the
 *  same Map reference when nothing material changed (so `patch` callers can
 *  skip cloning); otherwise a new Map with the changed buckets replaced.
 *
 *  `arrivalMs: null` = unknown-age seed (SSE snapshot rows carry no
 *  lastUpdateMs — seed the tenths without flashing recency). `passive`
 *  marks the 15s heartbeat re-carry: it only ingests when values actually
 *  moved (a catch-up after a dropped consensus event). */
function ingestTrendRows(
  prevMap: Map<string, ConsensusTrendState>,
  rows: OddsStreamConsensus[],
  nowMs: number,
  arrivalMs: number | null,
  passive: boolean,
): Map<string, ConsensusTrendState> {
  let next: Map<string, ConsensusTrendState> | null = null;
  for (const c of rows) {
    const key = consensusBucketKeyOf(c);
    const prev = (next ?? prevMap).get(key) ?? null;
    const ingested = ingestConsensusTrendFrame(
      prev,
      trendFrameFromConsensus(c, arrivalMs),
      nowMs,
      { passive },
    );
    if (ingested !== prev) {
      if (next == null) next = new Map(prevMap);
      next.set(key, ingested);
    }
  }
  return next ?? prevMap;
}

export interface OddsStreamHubState {
  snapshot: OddsStreamSnapshot | null;
  latestConsensus: OddsStreamConsensus[];
  latestGameState: OddsStreamGameState | null;
  /** Full-Game (`periodTypeId === 1`) history with live tail appended,
   *  sorted by ts ascending. Chart-ready. */
  consensusPoints: OddsStreamConsensusHistoryPoint[];
  status: OddsStreamStatus;
  droppedDueToBackpressure: number;
  lastHeartbeatMs: number | null;
  /** Sub-cent trend/recency state per consensus bucket, keyed
   *  `${periodTypeId}|${marketType ?? 'result'}`. Fed by live `consensus`
   *  events (tenth-only frames and sharpBookUsed flips included), seeded
   *  age-unknown (dim) from the snapshot, caught up passively off
   *  heartbeats. `consensusHistory` stays integer-grain and never feeds
   *  this. Render via `useConsensusGlow`. */
  consensusTrends: Map<string, ConsensusTrendState>;
  /** Latest line per (sourceId × betType × period × side). Seeded from
   *  snapshot.lines, updated on every `line` event. */
  linesByKey: Map<string, OddsStreamLine>;
  /** Rolling per-line history. Same key as linesByKey. Sliding window
   *  pruned to the last POINT_WINDOW_MS — used by both the table's
   *  fixed-N sparklines and the /sources page's time-axis chart, so
   *  one buffer feeds both surfaces.
   *
   *  Memory: at 1 update/sec (worst case during heavy moves) a single
   *  10-minute buffer is 600 entries × ~16 bytes ≈ 10 KB. Across
   *  ~30 sources × 6 keys (ML+spread+total × away/home) that's ~1.8 MB
   *  upper bound; in practice books push far fewer updates so real
   *  consumption is much lower. */
  sparklines: Map<string, SourcePoint[]>;
}

const EMPTY_STATE: OddsStreamHubState = {
  snapshot: null,
  latestConsensus: [],
  latestGameState: null,
  consensusPoints: [],
  status: 'idle',
  droppedDueToBackpressure: 0,
  lastHeartbeatMs: null,
  consensusTrends: new Map(),
  linesByKey: new Map(),
  sparklines: new Map(),
};

interface Subscription {
  refcount: number;
  handle: OddsStreamHandle | null;
  state: OddsStreamHubState;
  listeners: Set<() => void>;
  closeTimer: ReturnType<typeof setTimeout> | null;
}

const subscriptions = new Map<string, Subscription>();

function notify(sub: Subscription) {
  for (const l of sub.listeners) l();
}

function patch(sub: Subscription, partial: Partial<OddsStreamHubState>) {
  sub.state = { ...sub.state, ...partial };
}

/** Reasons already logged, so a route the trader revisits cannot fill the
 *  console. Keyed by reason — the absent-base case is deployment-wide, not
 *  per game — and never carrying the URL. */
const warnedReasons = new Set<string>();

function warnUnavailable(reason: string) {
  if (warnedReasons.has(reason)) return;
  warnedReasons.add(reason);
  // eslint-disable-next-line no-console
  console.warn('[oddsStream] consensus feed unavailable', { reason });
}

function openConnection(gameKey: string, sub: Subscription) {
  patch(sub, { status: 'connecting' });
  notify(sub);

  const handlers: OddsStreamHandlers = {
    onOpen: () => {
      patch(sub, { status: 'open' });
      notify(sub);
    },
    onSnapshot: (snap) => {
      // Seed consensus history (Full-Game only, sorted ascending). Same
      // bootstrap synthesis the old useOddsStream did — paint a flat
      // line at current value when hydra's ring buffer is empty.
      const seeded = snap.consensusHistory
        .filter((p) => p.periodTypeId === FULL_GAME_PERIOD_ID)
        .slice()
        .sort((a, b) => a.ts - b.ts);
      if (seeded.length === 0) {
        const currentFg = snap.consensus.find(isFullGameResult);
        if (currentFg) {
          seeded.push({
            ts: snap.serverTs,
            periodTypeId: FULL_GAME_PERIOD_ID,
            awayFairProbCents: currentFg.awayFairProbCents,
            homeFairProbCents: currentFg.homeFairProbCents,
            drawFairProbCents: currentFg.drawFairProbCents,
            contributingBooks: currentFg.contributingBooks,
          });
        }
      }
      // Mirror image of the consensusPoints seeding above: if the
      // snapshot's *current* consensus carries no Full-Game entry, seed
      // one from the most-recent FG history point. The push channel only
      // emits on a ≥1¢ shift, so a quiet game's snapshot can ship FG
      // history (the chart line) while `consensus` is empty — leaving the
      // U-row + chart labels (which key on latestConsensus.find(FG)) blank
      // until the first delta lands many seconds later. This uses hydra's
      // own last-known value (not a fabricated number) so those surfaces
      // render immediately; the first real `consensus` event overwrites it
      // via mergeConsensusByPeriod.
      let latestConsensus = snap.consensus;
      const hasCurrentFg = latestConsensus.some(isFullGameResult);
      if (!hasCurrentFg && seeded.length > 0) {
        const lastFg = seeded[seeded.length - 1];
        latestConsensus = [
          ...latestConsensus,
          {
            periodTypeId: FULL_GAME_PERIOD_ID,
            periodLabel: 'Full Game',
            awayFairProbCents: lastFg.awayFairProbCents,
            homeFairProbCents: lastFg.homeFairProbCents,
            drawFairProbCents: lastFg.drawFairProbCents,
            contributingBooks: lastFg.contributingBooks,
          },
        ];
      }

      // Seed per-line cache + sparklines from snapshot.lines. Each line
      // becomes a single (ts, cents) anchor point; subsequent `line`
      // events grow each buffer, sliding-window pruned to POINT_WINDOW_MS.
      const linesByKey = new Map<string, OddsStreamLine>();
      const sparklines = new Map<string, SourcePoint[]>();
      for (const line of snap.lines) {
        const key = lineKeyOf(line);
        linesByKey.set(key, line);
        sparklines.set(key, [{ ts: line.lastUpdateMs || snap.serverTs, cents: line.impliedProbCents }]);
      }
      // Seed trend state from the snapshot's raw rows (not the synthetic
      // FG fallback above — it's rebuilt from integer-grain history and
      // would plant a false tenths baseline). Snapshot rows carry no
      // lastUpdateMs, so seed age-unknown: prevTenths/sharp land, but no
      // recency flash and no fabricated deltas — trend builds from live
      // frames only.
      const consensusTrends = ingestTrendRows(
        sub.state.consensusTrends,
        snap.consensus,
        Date.now(),
        null,
        true,
      );
      patch(sub, {
        snapshot: snap,
        latestConsensus,
        latestGameState: snap.gameState,
        consensusPoints: seeded,
        consensusTrends,
        linesByKey,
        sparklines,
      });
      notify(sub);
    },
    onConsensus: (event) => {
      const fg = event.consensus.find(isFullGameResult);
      const nextPoints = fg
        ? [
            ...sub.state.consensusPoints,
            {
              ts: event.serverTs,
              periodTypeId: FULL_GAME_PERIOD_ID,
              awayFairProbCents: fg.awayFairProbCents,
              homeFairProbCents: fg.homeFairProbCents,
              drawFairProbCents: fg.drawFairProbCents,
              contributingBooks: fg.contributingBooks,
            },
          ]
        : sub.state.consensusPoints;
      // Live frames are the trend feed proper: every one is a genuine
      // update (tenth-only moves and sharp flips included), dated at
      // receipt.
      const now = Date.now();
      patch(sub, {
        latestConsensus: mergeConsensusByPeriod(sub.state.latestConsensus, event.consensus),
        consensusPoints: nextPoints,
        consensusTrends: ingestTrendRows(
          sub.state.consensusTrends,
          event.consensus,
          now,
          now,
          false,
        ),
      });
      notify(sub);
    },
    onGameState: (event) => {
      patch(sub, { latestGameState: event.state });
      notify(sub);
    },
    onHeartbeat: (event) => {
      // Append a synthetic Full-Game point during quiet trading so the
      // chart's right edge keeps advancing. Dedup if a consensus event
      // landed in the same window.
      const fg = event.consensus.find(isFullGameResult);
      let nextPoints = sub.state.consensusPoints;
      if (fg) {
        const lastTs = nextPoints.length > 0 ? nextPoints[nextPoints.length - 1].ts : 0;
        if (event.serverTs - lastTs >= HEARTBEAT_DEDUP_MS) {
          nextPoints = [
            ...nextPoints,
            {
              ts: event.serverTs,
              periodTypeId: FULL_GAME_PERIOD_ID,
              awayFairProbCents: fg.awayFairProbCents,
              homeFairProbCents: fg.homeFairProbCents,
              drawFairProbCents: fg.drawFairProbCents,
              contributingBooks: fg.contributingBooks,
            },
          ];
        }
      }
      // Heartbeats re-carry the latest snapshot every 15s — NOT fresh
      // computes. Passive ingest: trend/recency only move if the values
      // actually did (a catch-up after a dropped consensus event), so the
      // glow can't be kept alive by the heartbeat alone.
      const now = Date.now();
      patch(sub, {
        latestConsensus: mergeConsensusByPeriod(sub.state.latestConsensus, event.consensus),
        droppedDueToBackpressure: event.droppedDueToBackpressure,
        lastHeartbeatMs: event.serverTs,
        status: 'open',
        consensusPoints: nextPoints,
        consensusTrends: ingestTrendRows(
          sub.state.consensusTrends,
          event.consensus,
          now,
          now,
          true,
        ),
      });
      notify(sub);
    },
    onLine: (event) => {
      const key = lineKeyOf(event.line);
      // Replace the line; append the new (ts, cents) sample and prune
      // anything outside the 10-min window. Both Maps are cloned so
      // useSyncExternalStore consumers detect change via reference
      // identity. ts uses the line's lastUpdateMs when present (book's
      // own modifiedOn) so chart x-axis reflects real movement times,
      // not relay arrival times.
      const nextLines = new Map(sub.state.linesByKey);
      nextLines.set(key, event.line);
      const nextSpark = new Map(sub.state.sparklines);
      const prior = nextSpark.get(key) ?? [];
      const ts = event.line.lastUpdateMs || event.serverTs;
      const cutoff = ts - POINT_WINDOW_MS;
      const pruned = prior.filter((p) => p.ts >= cutoff);
      pruned.push({ ts, cents: event.line.impliedProbCents });
      nextSpark.set(key, pruned);
      patch(sub, { linesByKey: nextLines, sparklines: nextSpark });
      notify(sub);
    },
    onError: () => {
      patch(sub, { status: 'error' });
      notify(sub);
    },
  };

  // The optional stream has two ways of not opening: no analytics base
  // configured (null) and a base the EventSource constructor rejects (throw).
  // Both are caught HERE rather than at the raise site because `subscriptions`
  // is a process-wide singleton: before #866 the exception escaped `subscribe`
  // — i.e. escaped `useSyncExternalStore`'s effect, taking the whole Game
  // Detail view with it — and left a half-built entry stuck at 'connecting'
  // that every later subscriber joined and waited on forever.
  let handle: OddsStreamHandle | null = null;
  try {
    handle = openOddsStream(gameKey, handlers);
    if (!handle) warnUnavailable('not-configured');
  } catch (err) {
    warnUnavailable(err instanceof Error ? err.name : 'open-failed');
  }

  if (!handle) {
    // Terminal, and the entry STAYS in the map: consumers read an honest
    // status off it, while the refcount path below still tears it down, so a
    // later subscription starts clean.
    patch(sub, { status: 'unavailable' });
    notify(sub);
    return;
  }
  sub.handle = handle;
}

/** Subscribe a listener to a gameKey's stream. Opens the EventSource
 *  on the first subscriber, closes it after a grace window when the
 *  last subscriber leaves. Returns an unsubscribe function. */
export function subscribe(gameKey: string, callback: () => void): () => void {
  let sub = subscriptions.get(gameKey);
  if (!sub) {
    sub = {
      refcount: 0,
      handle: null,
      state: EMPTY_STATE,
      listeners: new Set(),
      closeTimer: null,
    };
    subscriptions.set(gameKey, sub);
    openConnection(gameKey, sub);
  } else if (sub.handle === null && sub.state.status === 'unavailable') {
    // A new subscriber is a new chance: an entry that never opened retries
    // here instead of waiting for the last leaver's grace window. Only
    // 'unavailable' qualifies — 'error' and 'closed' still hold a live
    // EventSource that reconnects itself, and re-opening those would double
    // the socket against hydra's 256-listener cap.
    openConnection(gameKey, sub);
  }
  // Cancel any pending close — a fresh subscriber means we're staying.
  if (sub.closeTimer != null) {
    clearTimeout(sub.closeTimer);
    sub.closeTimer = null;
  }
  sub.refcount++;
  sub.listeners.add(callback);
  return () => {
    if (!sub) return;
    sub.listeners.delete(callback);
    sub.refcount--;
    if (sub.refcount <= 0) {
      // Grace window: defer close so React 18 StrictMode's intentional
      // mount/unmount/remount in dev doesn't churn the SSE, and a
      // rapid /game ⇄ /sources navigation reuses the same connection.
      sub.closeTimer = setTimeout(() => {
        const current = subscriptions.get(gameKey);
        if (current === sub && current.refcount <= 0) {
          current.handle?.close();
          subscriptions.delete(gameKey);
        }
      }, CLOSE_GRACE_MS);
    }
  };
}

/** Read the current state for a gameKey. Stable reference between
 *  events — useSyncExternalStore relies on this for change detection. */
export function getSnapshot(gameKey: string | null): OddsStreamHubState {
  if (!gameKey) return EMPTY_STATE;
  return subscriptions.get(gameKey)?.state ?? EMPTY_STATE;
}
